/**
 * Admin Revenue Page
 * Every money stream on Eqaya, unified: Property Check fees, call-out visit
 * fees, invoices, realized platform commissions, escrow fees, and the
 * verifier-earnings cost ledger. Answers "how much have we collected, and
 * where is it recorded?" without a database session.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Box, Typography, Breadcrumbs, Link, Card, CardContent,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, ToggleButton,
  ToggleButtonGroup, CircularProgress, Alert, Grid, Tooltip
} from '@mui/material';
import {
  NavigateNext, HomeWork, DirectionsCar, ReceiptLong, Percent,
  AccountBalance, Payments
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const fmtWhen = (d) => d ? new Date(d).toLocaleString('en-GB', {
  timeZone: 'Africa/Harare', day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit'
}) : '—';

const fmtMoney = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  const prefix = currency && currency !== 'USD' ? `${currency} ` : '$';
  return `${prefix}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const statusColor = (s) => ({
  paid: 'success', released: 'success', completed: 'success', funded: 'success',
  paid_held: 'info', held: 'info', active: 'info', owed: 'warning',
  pending_payment: 'warning', pending: 'warning', sent: 'default',
  declined: 'error', cancelled: 'error', disputed: 'error', void: 'error',
  refunded: 'error'
}[String(s || '').toLowerCase()] || 'default');

const prettyStatus = (s) => String(s || '—').replace(/_/g, ' ');

const commissionSourceLabel = (s) => ({
  visit_callout_fee: 'Call-out fee',
  invoice: 'Invoice',
  standalone_verification: 'Property Check / verification',
}[s] || prettyStatus(s));

const SummaryCard = ({ label, value, hint, color }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Tooltip title={hint || ''} arrow>
      <Card variant="outlined">
        <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </CardContent>
      </Card>
    </Tooltip>
  </Grid>
);

const Section = ({ icon, title, subtitle, count, columns, rows, renderRow, emptyText }) => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
        <Chip size="small" label={count} color={count > 0 ? 'primary' : 'default'} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitle}</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{emptyText}</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => <TableCell key={c} sx={{ fontWeight: 600 }}>{c}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>{rows.map(renderRow)}</TableBody>
          </Table>
        </Box>
      )}
    </CardContent>
  </Card>
);

// `embedded` renders without breadcrumbs/outer container — used as a tab
// inside AdminDashboard; the standalone /admin/revenue route also lands there.
const AdminRevenuePage = ({ embedded = false }) => {
  const navigate = useNavigate();
  // Default to all time: platform revenue is sparse early on, and a $0
  // 30-day first view reads as "no revenue recorded" (it did to the owner).
  const [days, setDays] = useState(3650);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/revenue?days=${d}`);
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const s = data?.summary || {};
  const t = data?.totals || {};

  return (
    <Container maxWidth="lg" sx={embedded ? { py: 0, px: 0 } : { py: 3 }} disableGutters={embedded}>
      {!embedded && (
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/dashboard')}>Admin</Link>
          <Typography color="text.primary">Revenue</Typography>
        </Breadcrumbs>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Revenue</Typography>
          <Typography variant="body2" color="text.secondary">
            Money collected and owed across every stream — times in Zimbabwe time
          </Typography>
        </Box>
        <ToggleButtonGroup value={days} exclusive size="small"
          onChange={(_, v) => v && setDays(v)}>
          <ToggleButton value={7}>7 days</ToggleButton>
          <ToggleButton value={30}>30 days</ToggleButton>
          <ToggleButton value={90}>90 days</ToggleButton>
          <ToggleButton value={3650}>All time</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {!loading && !error && data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3, mt: 0.5 }}>
            <SummaryCard label="Gross collected" value={fmtMoney(s.grossCollected)}
              color="success.main"
              hint={`Property Checks ${fmtMoney(s.grossBreakdown?.propertyChecks)} · call-out fees ${fmtMoney(s.grossBreakdown?.visitFees)} · invoices ${fmtMoney(s.grossBreakdown?.invoices)}`} />
            <SummaryCard label="Platform commission" value={fmtMoney(s.platformCommission)}
              color="success.main"
              hint={`Realized 3% cuts ${fmtMoney(s.commissionBreakdown?.realizedCuts)} · escrow fees ${fmtMoney(s.commissionBreakdown?.escrowFees)}`} />
            <SummaryCard label="Held, not released" value={fmtMoney(s.heldNotReleased)}
              hint="Charged to clients but not yet released to the provider" />
            <SummaryCard label="Awaiting payment" value={fmtMoney(s.awaitingPayment)}
              hint="Property Checks booked but never paid (abandoned checkouts)" />
            <SummaryCard label="Owed to verifiers" value={fmtMoney(s.verifierOwed)}
              color="warning.main" hint="Accrued verifier earnings not yet paid out" />
            <SummaryCard label="Paid to verifiers" value={fmtMoney(s.verifierPaid)}
              hint="Verifier earnings already paid out" />
          </Grid>

          <Section
            icon={<HomeWork color="primary" />}
            title="Property Checks" count={t.propertyChecks}
            subtitle="Fees charged via Stripe Checkout when owners book a Property Check"
            columns={['When', 'Client', 'Package', 'Amount', 'Payment', 'Job status', 'Reference']}
            rows={data.propertyChecks} emptyText="No Property Checks in this window."
            renderRow={(r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtWhen(r.created_at)}</TableCell>
                <TableCell>
                  {r.client_name || '—'}
                  <Typography variant="caption" color="text.secondary" display="block">{r.client_email}</Typography>
                </TableCell>
                <TableCell>{prettyStatus(r.package_slug)}</TableCell>
                <TableCell>{fmtMoney(r.amount, r.currency)}</TableCell>
                <TableCell><Chip size="small" label={prettyStatus(r.payment_status)} color={statusColor(r.payment_status)} /></TableCell>
                <TableCell><Chip size="small" label={prettyStatus(r.request_status)} color={statusColor(r.request_status)} /></TableCell>
                <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={r.payment_reference || ''}><span>{r.payment_reference || '—'}</span></Tooltip>
                </TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<DirectionsCar color="primary" />}
            title="Call-out visit fees" count={t.visitFees}
            subtitle="Contractor call-out fees on quote-request visits (3% platform cut on release)"
            columns={['Paid', 'Contractor', 'Client', 'Description', 'Amount', 'Status']}
            rows={data.visitFees} emptyText="No call-out fees in this window."
            renderRow={(r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtWhen(r.paid_at || r.released_at)}</TableCell>
                <TableCell>{r.contractor_name || '—'}</TableCell>
                <TableCell>{r.client_name || '—'}</TableCell>
                <TableCell>{r.description || '—'}</TableCell>
                <TableCell>{fmtMoney(r.amount)}</TableCell>
                <TableCell><Chip size="small" label={prettyStatus(r.fee_status)} color={statusColor(r.fee_status)} /></TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<ReceiptLong color="primary" />}
            title="Invoices" count={t.invoices}
            subtitle="Impromptu provider-to-client invoices (3% platform cut on release)"
            columns={['Created', 'Invoice', 'Issuer', 'Client', 'Amount', 'Status']}
            rows={data.invoices} emptyText="No invoices in this window."
            renderRow={(r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtWhen(r.created_at)}</TableCell>
                <TableCell>
                  {r.invoice_number}
                  <Typography variant="caption" color="text.secondary" display="block">{r.title}</Typography>
                </TableCell>
                <TableCell>{r.issuer_company || r.issuer_name || '—'}</TableCell>
                <TableCell>{r.client_name || '—'}</TableCell>
                <TableCell>{fmtMoney(r.amount, r.currency)}</TableCell>
                <TableCell><Chip size="small" label={prettyStatus(r.status)} color={statusColor(r.status)} /></TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<Percent color="primary" />}
            title="Platform commissions" count={t.commissions}
            subtitle="Realized platform cuts recorded when held money is released"
            columns={['When', 'Source', 'Gross', 'Platform cut']}
            rows={data.commissions} emptyText="No realized commissions in this window."
            renderRow={(r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtWhen(r.created_at)}</TableCell>
                <TableCell>{commissionSourceLabel(r.source)}</TableCell>
                <TableCell>{fmtMoney(r.gross, r.currency)}</TableCell>
                <TableCell>{fmtMoney(r.amount, r.currency)}</TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<AccountBalance color="primary" />}
            title="Project escrow" count={t.escrows}
            subtitle="Funded project escrow accounts — client money held for contractors; the platform fee is Eqaya's"
            columns={['Created', 'Client', 'Purpose', 'Total funded', 'Platform fee', 'Status']}
            rows={data.escrows} emptyText="No escrow accounts in this window."
            renderRow={(r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtWhen(r.created_at)}</TableCell>
                <TableCell>{r.client_name || '—'}</TableCell>
                <TableCell>{r.purpose || '—'}</TableCell>
                <TableCell>{fmtMoney(r.total_amount)}</TableCell>
                <TableCell>{fmtMoney(r.platform_fee_amount)}</TableCell>
                <TableCell><Chip size="small" label={prettyStatus(r.status)} color={statusColor(r.status)} /></TableCell>
              </TableRow>
            )}
          />

          <Section
            icon={<Payments color="primary" />}
            title="Verifier earnings (costs)" count={t.verifierEarnings}
            subtitle="What Eqaya owes or has paid verifiers for contractor verifications"
            columns={['Accrued', 'Verifier', 'Amount', 'Status', 'Paid at']}
            rows={data.verifierEarnings} emptyText="No verifier earnings in this window."
            renderRow={(r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmtWhen(r.created_at)}</TableCell>
                <TableCell>{r.verifier_name || '—'}</TableCell>
                <TableCell>{fmtMoney(r.amount)}</TableCell>
                <TableCell><Chip size="small" label={prettyStatus(r.status)} color={statusColor(r.status)} /></TableCell>
                <TableCell>{fmtWhen(r.paid_at)}</TableCell>
              </TableRow>
            )}
          />
        </>
      )}
    </Container>
  );
};

export default AdminRevenuePage;
